// @flow

import * as React from 'react';
import Group from 'sail/Group';
import Interactive from 'sail/Interactive';
import {Text} from 'sail/Text';
import Icon from 'sail/Icon';
import {UnstyledLink} from 'sail/UnstyledLink';
import {useLocation, useAnalytics} from 'docs/data';
import {defineMessages, FormattedMessage} from 'react-intl';

import type {IconType} from 'sail/Icon/types';
import {ColorBadge} from './ColorBadge';

const messages = defineMessages({
  pendingForTranslation: {
    id: 'docsShell.sidebarNavigation.badge.pending',
    description: 'Tooltip displayed for untranslated documents',
    defaultMessage: 'Page pending for translation',
  },
});

type SidebarLinkProps = {|
  children: React.Node,
  href: string,
  icon?: ?IconType,
  title?: ?string,
  active?: ?boolean,
  translated: ?boolean,
|};

export const SidebarLink = ({
  children,
  href,
  icon,
  title,
  active = false,
  translated = null,
}: SidebarLinkProps) => {
  const {getLinkInfo, location} = useLocation();
  const {trackAction} = useAnalytics();
  const link = getLinkInfo(href);

  const getTextColor = (mouse) => {
    if (active) {
      return 'blue';
    } else {
      return mouse === 'hover' || mouse === 'active' ? 'default' : 'gray';
    }
  };

  const getTextWeight = (): 'medium' | 'regular' => {
    if (active) {
      return 'medium';
    } else {
      return 'regular';
    }
  };

  const addColorBadge = (content) => {
    // translated can be null and we don't want to display the badge in this case
    if (translated !== false) {
      return content;
    }
    const message = <FormattedMessage {...messages.pendingForTranslation} />;
    return (
      <ColorBadge position="next" message={message}>
        {content}
      </ColorBadge>
    );
  };

  const getRightIcon = (mouse) => {
    if (link.isExternal && (mouse === 'hover' || mouse === 'active')) {
      return <Icon icon="external" size={12} padding={4} color="default" />;
    }
  };

  return (
    <Interactive>
      {({mouse, focus}) => {
        const content = (
          <Text
            color={getTextColor(mouse)}
            weight={getTextWeight()}
            lineHeight={20}
          >
            <UnstyledLink
              href={href}
              title={title}
              onClick={() => {
                trackAction('docs_nav_clicked', {
                  link: href,
                  docsHistory: location.pathname,
                });
              }}
            >
              <Group direction="horizontal" spacing={{horizontal: 4}}>
                {icon && <Icon icon={icon} size={12} padding={4} />}
                {children}
                {getRightIcon(mouse)}
              </Group>
            </UnstyledLink>
          </Text>
        );
        return addColorBadge(content);
      }}
    </Interactive>
  );
};
